import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronDown, RotateCcw, Check, Copy } from "lucide-react";
import { api, money } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { BottomNav } from "@/components/BottomNav";

const STATUSES = ["Order Received", "Processing", "Ready", "Delivered"];
const STATUS_COLORS = {
  "Order Received": "bg-[#DDF0E5] text-[#0B7A3B]",
  Processing: "bg-amber-100 text-amber-700",
  Ready: "bg-blue-100 text-blue-700",
  Delivered: "bg-[#0B7A3B] text-white",
};

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [bank, setBank] = useState(null);
  const { addItem } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/orders").then(({ data }) => setOrders(data)).catch(() => {}).finally(() => setLoading(false));
    api.get("/bank-details").then(({ data }) => setBank(data)).catch(() => {});
  }, []);

  const copy = (label, value) => {
    const done = () => toast.success(`${label} copied`);
    navigator.clipboard?.writeText(value).then(done).catch(() => {
      const ta = document.createElement("textarea");
      ta.value = value; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); done(); } catch { toast.error("Copy failed"); }
      document.body.removeChild(ta);
    });
  };

  const reorder = async (order) => {
    const { data } = await api.get("/products");
    const map = Object.fromEntries(data.map((p) => [p.product_id, p]));
    let added = 0;
    order.items.forEach((i) => {
      const p = map[i.product_id];
      if (p && p.available) { addItem(p, i.quantity); added++; }
    });
    if (added > 0) { toast.success(`${added} item(s) added to basket`); navigate("/cart"); }
    else toast.error("None of these items are available right now");
  };

  return (
    <div className="max-w-md mx-auto min-h-screen bg-[#F5F8F6] pb-24">
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-xl border-b border-[#F5F8F6] px-4 py-3 pt-safe">
        <h1 className="text-xl font-extrabold text-[#122117]">Your Orders</h1>
      </header>

      <div className="px-4 pt-4 space-y-3">
        {loading ? (
          <p className="text-center text-[#6B8375] py-10">Loading…</p>
        ) : orders.length === 0 ? (
          <div className="text-center py-20" data-testid="orders-empty">
            <p className="text-[#6B8375]">No orders yet.</p>
            <button onClick={() => navigate("/market")} className="mt-5 bg-[#0B7A3B] text-white font-bold rounded-full px-6 py-3">Start shopping</button>
          </div>
        ) : (
          orders.map((o) => {
            const isOpen = open === o.order_id;
            const stepIdx = STATUSES.indexOf(o.status);
            return (
              <div key={o.order_id} className="bg-white rounded-[18px] overflow-hidden" data-testid={`order-${o.order_number}`}>
                <button onClick={() => setOpen(isOpen ? null : o.order_id)} className="w-full p-4 flex items-center justify-between" data-testid={`order-toggle-${o.order_number}`}>
                  <div className="text-left">
                    <div className="flex items-center gap-2">
                      <span className="font-extrabold text-[#122117]">#{o.order_number}</span>
                      <span className={`text-[10px] uppercase tracking-wide font-bold rounded-full px-2 py-0.5 ${STATUS_COLORS[o.status]}`}>{o.status}</span>
                    </div>
                    <p className="text-xs text-[#6B8375] mt-0.5">{new Date(o.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })} · {money(o.grand_total)}</p>
                  </div>
                  <ChevronDown size={20} className={`text-[#6B8375] transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>

                {isOpen && (
                  <div className="px-4 pb-4 pm-fade-up">
                    {/* Timeline */}
                    <div className="flex items-center justify-between mb-4 mt-1">
                      {STATUSES.map((s, i) => (
                        <React.Fragment key={s}>
                          <div className="flex flex-col items-center gap-1 flex-1">
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center ${i <= stepIdx ? "bg-[#0B7A3B] text-white" : "bg-[#F5F8F6] text-[#6B8375]"}`}>
                              {i <= stepIdx ? <Check size={14} /> : <span className="text-xs">{i + 1}</span>}
                            </div>
                            <span className={`text-[9px] text-center leading-tight ${i <= stepIdx ? "text-[#0B7A3B] font-bold" : "text-[#6B8375]"}`}>{s}</span>
                          </div>
                          {i < STATUSES.length - 1 && <div className={`h-0.5 flex-1 -mt-4 ${i < stepIdx ? "bg-[#0B7A3B]" : "bg-[#F5F8F6]"}`} />}
                        </React.Fragment>
                      ))}
                    </div>

                    <div className="space-y-1.5 bg-[#F5F8F6] rounded-[12px] p-3">
                      {o.items.map((i) => (
                        <div key={i.product_id} className="flex justify-between text-sm">
                          <span className="text-[#122117]">{i.name} × {i.quantity}</span>
                          <span className="font-semibold">{money(i.price * i.quantity)}</span>
                        </div>
                      ))}
                      <div className="h-px bg-white my-1.5" />
                      <div className="flex justify-between text-xs text-[#6B8375]"><span>{o.charge_label}</span><span>{money(o.charge)}</span></div>
                      <div className="flex justify-between font-extrabold"><span>Total</span><span className="text-[#0B7A3B]">{money(o.grand_total)}</span></div>
                    </div>

                    <p className="text-xs text-[#6B8375] mt-3">Deliver to: {o.address}, {o.postcode}</p>

                    {bank && (
                      <div className="mt-3 bg-[#DDF0E5] rounded-[12px] p-3" data-testid={`payment-details-${o.order_number}`}>
                        <p className="font-extrabold text-[#0B7A3B] text-sm mb-1.5">Pay by bank transfer</p>
                        {[
                          ["Bank", bank.bank],
                          ["Account name", bank.account_name],
                          ["Sort code", bank.sort_code],
                          ["Account number", bank.account_number],
                          ["Reference", `ORDER-${o.order_number}`],
                        ].map(([label, value]) => (
                          <button
                            key={label}
                            onClick={() => copy(label, value)}
                            className="w-full flex items-center justify-between py-1.5 active:scale-[0.98] transition-transform"
                            data-testid={`copy-${label.replace(/\s/g, "").toLowerCase()}-${o.order_number}`}
                          >
                            <span className="text-xs text-[#6B8375]">{label}</span>
                            <span className="flex items-center gap-1.5 text-sm font-bold text-[#122117]">{value} <Copy size={12} className="text-[#0B7A3B]" /></span>
                          </button>
                        ))}
                      </div>
                    )}

                    <button onClick={() => reorder(o)} className="mt-3 w-full flex items-center justify-center gap-2 bg-[#DDF0E5] text-[#0B7A3B] font-bold rounded-full py-3 active:scale-95 transition-transform" data-testid={`reorder-${o.order_number}`}>
                      <RotateCcw size={16} /> Reorder
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
      <BottomNav />
    </div>
  );
}
